import Scroll from 'react-scroll';
import _ from 'lodash';
import {
  formatDirectUrl,
  isSpecial,
  isArticleTab,
  slugify,
  urlIsExternal,
  PAGE_TYPE_CREDITS,
  PANEL_TYPE_PROMOS,
  LABEL_CLIPS_AND_EXTRAS,
} from 'helpers/ContentHelper';
import { getArticleApiId } from 'components/Article/ArticleHelpers';

import { helperGetShowUrl } from 'helpers/RouteHelper';
import Regex from 'helpers/Regex';
import { isHiddenForAnonymous } from 'helpers/AnonymousHelper';
import { getPersonalityTabSlug } from './PersonalityDetailHelpers';

const SERIES_TYPE_SPECIAL = 'special';
export const SERIES_TYPE_SERIES = 'series';
export const SERIES_TYPE_CREDITS = 'credits';
export const SERIES_TYPE_SERIES_COLLECTION = 'seriesCollection';

export const isSeriesCollection = seriesType => seriesType === SERIES_TYPE_SERIES_COLLECTION;

export const PANEL_TYPE_EPISODES = 'Season';
export const PANEL_TYPE_CLIPS = 'VideoList';
export const PANEL_TYPE_SCREEN_PANEL = 'ScreenPanel';
export const PANEL_TYPE_CAST = 'Cast';
const PANEL_TYPE_SERIES = 'Series';

export const PROMO_TYPE_FEATURED_TEXT = 'featuredText';
export const PROMO_TYPE_LEAD = 'lead';
export const PROMO_TYPE_INSET = 'inset';
export const PROMO_TYPE_MARKETING_VIDEO = 'marketingVideoBanner';
const PROMO_TYPE_NAV_ITEM = 'nav-item';
export const PROMO_TYPE_FEATURED_OVERRIDE = 'featured-override';
export const PROMO_TYPE_HEADER = 'vote';
const PROMO_TYPE_CALENDAR = 'calendar';
export const PROMO_TYPE_GALLERY = 'gallery';
export const PROMO_TYPE_HERO = 'hero';
export const PROMO_MARKETING_PERKS = 'marketing-perks';

export const NO_TAB_STATE_TRAILER = 'NO_TAB_STATE_TRAILER';

export const SERIES_SCROLL_ELEMENT = 'SERIES_SCROLL_ELEMENT';
export const ARTICLE_SCROLL_ELEMENT = 'ARTICLE_SCROLL_ELEMENT';

export const TILE_TYPE_CONTINUE_WATCHING = 'CONTINUE_WATCHING';
export const TILE_TYPE_LIVE = 'LIVE';
export const TILE_TYPE_SERIES_PROMO = 'SERIES_PROMO';
export const TILE_TYPE_LATEST = 'LATEST';
export const TILE_TYPE_LATEST_PERSONALITY = 'LATEST_PERSONALITY';

const GROUPED_PANELS = [PANEL_TYPE_EPISODES, PANEL_TYPE_SERIES];

const enableBioLanding = true;

export const isTabGrouped = ({ tabType, seriesType }) => {
  if (seriesType === SERIES_TYPE_SERIES_COLLECTION || isSpecial(seriesType)) return false;
  return GROUPED_PANELS.indexOf(tabType) >= 0;
};

export const getTabName = ({ tab, seriesType }) => {
  if (!tab) return null;
  const {
    '@type': type,
    seasonNumber,
    name,
    ctaText,
    headline,
    displaySeasonAndEpisodeCounts,
  } = tab;
  const {
    seriesDetail_seasonLabel: seasonLabel,
    seriesDetail_castHeader: creditsLabel,
  } = window.Language;

  if (type === PANEL_TYPE_CAST) return creditsLabel;
  if (seriesType === SERIES_TYPE_SERIES_COLLECTION) return name;
  if (type === PANEL_TYPE_EPISODES && headline && !displaySeasonAndEpisodeCounts) return headline;
  if (type === PANEL_TYPE_EPISODES && !headline) return `${seasonLabel} ${seasonNumber}`;
  return headline || ctaText || name;
};

export const getTabSlug = ({ tab, tabs, noTabHomepage, seriesType, isSubmenu }) => {
  if (!tab) return null;
  const { '@type': type } = tab;
  const headline = getTabName({ tab, seriesType })
    .toString()
    .toLowerCase()
    .replace(Regex.SPACES, '-') // Replace spaces with -
    .replace(Regex.NON_WORD_CHARACTERS, '') // Remove all non-word chars
    .replace(Regex.MULTIPLE_DASHES, '-') // Replace multiple - with single -
    .replace(Regex.LEADING_DASH, '') // Trim - from start of text
    .replace(Regex.TRAILING_DASH, ''); // Trim - from end of text
  const directUrl = _.get(tab, 'url');
  if (directUrl) {
    const firstDirectUrl = _.get(tabs, '0.url');
    if (directUrl && !urlIsExternal(directUrl) && firstDirectUrl === directUrl && !isSubmenu)
      return '';
  }
  if (tab && tabs) {
    if (!noTabHomepage && tabs.indexOf(tab) === 0) {
      return '';
    }
    const tabType = _.get(tab, '@type', null);
    const firstTabType = _.get(tabs, '0.@type', null);
    if (tabType === firstTabType && isTabGrouped({ tabType, seriesType })) {
      return tabType === PANEL_TYPE_EPISODES ? headline : '';
    }
  }
  if (type === PANEL_TYPE_CLIPS) return 'clips';
  if (type === PANEL_TYPE_EPISODES) return 'episodes';
  if (type === PANEL_TYPE_CAST) return 'credits';
  if (!headline) return null;
  return headline;
};

export const getTabPageId = ({ noTabHomepage, tab, tabs }) => {
  const { '@type': type, name, filter } = tab;
  if (!noTabHomepage && tabs.indexOf(tab) === 0) return '';
  if (type === PANEL_TYPE_CLIPS && filter) return `/${_.kebabCase(name)}`;
  return '';
};

export const getTabUrl = ({ showId, tab, tabs, noTabHomepage, seriesType, isSubmenu }) => {
  const directUrl = _.get(tab, 'url');
  if (directUrl && urlIsExternal(directUrl)) return formatDirectUrl(directUrl);
  const tabSlug = getTabSlug({ tab, tabs, noTabHomepage, seriesType, isSubmenu });
  const tabPageId = getTabPageId({ noTabHomepage, tab, tabs });
  let url = helperGetShowUrl(showId);
  if (tabSlug) url = `${url}${tabSlug}${tabPageId}/`;
  return url;
};

const isTabSelected = ({
  isPersonality,
  tab,
  tabs,
  noTabState,
  noTabHomepage,
  seriesType,
  pageId,
}) => {
  if (!tab) return false;
  const tabSlug = isPersonality
    ? getPersonalityTabSlug({ tab, tabs, noTabHomepage, seriesType })
    : getTabSlug({ tab, tabs, noTabHomepage, seriesType });
  const tabPageId = getTabPageId({ noTabHomepage, tab, tabs });
  if (pageId === tabSlug) return true;
  if (tabSlug === PAGE_TYPE_CREDITS && _.includes(pageId, tabSlug)) return true;
  if (pageId === `${tabSlug}${tabPageId}`) return true;
  if (noTabState) return false;
  if (!pageId) return true;
  return false;
};

export const hasChildren = tab => {
  const children = _.get(tab, 'children', null);
  return children && children.length > 0;
};

export const getSelectedTabIndex = ({
  isPersonality,
  pageId,
  tabs,
  noTabState,
  noTabHomepage,
  seriesType,
}) => {
  let subTabIndex = false;
  const tabIndex = tabs.findIndex(tab => {
    let selected = false;
    selected = isTabSelected({
      pageId,
      tab,
      tabs,
      noTabState,
      noTabHomepage,
      seriesType,
      isPersonality,
    });
    if (!selected && hasChildren(tab)) {
      const childTabs = _.get(tab, 'children');
      const { tabIndex: childIndex } = getSelectedTabIndex({
        pageId,
        tabs: childTabs,
        noTabState,
        noTabHomepage,
        seriesType,
      });
      if (childIndex !== false) {
        subTabIndex = childIndex;
        selected = true;
      }
    }
    return selected;
  });
  if (tabIndex < 0) {
    return false;
  }
  return { tabIndex, subTabIndex };
};

export const getPromoData = ({ data, promoType }) =>
  _.find(data, promo => {
    if (promoType === PROMO_MARKETING_PERKS) {
      const collectionType = _.get(promo, 'collectionType', '');
      return collectionType === PROMO_MARKETING_PERKS;
    }
    const type = _.get(promo, 'items.member[0].promoType', '');
    return type === promoType;
  });

export function clearFetchNodeInterval() {
  const { currentFetchNode } = this.state;
  const currentFetchInterval = _.get(currentFetchNode, 'fetch');
  if (currentFetchNode) {
    clearInterval(currentFetchInterval);
    this.setState({ currentFetchNode: null });
  }
}

export function checkForDOMNode(nextNodeId, duration, offsetTop) {
  const { currentFetchNode } = this.state;
  const currentNodeId = _.get(currentFetchNode, 'id');
  const currentFetchInterval = _.get(currentFetchNode, 'fetch');
  const nodeIdsMatch = currentNodeId === nextNodeId;
  if (!nodeIdsMatch) {
    if (currentFetchNode) {
      clearInterval(currentFetchInterval);
      this.setState({ currentFetchNode: null });
    }
    let count = 0;
    const fetchNodeInterval = setInterval(() => {
      count += 1;
      const node = document.getElementById(nextNodeId);
      if (node) {
        const nodeTop = node.offsetTop;
        Scroll.animateScroll.scrollTo(nodeTop + offsetTop);
        clearInterval(fetchNodeInterval);
        this.setState({ currentFetchNode: null });
      }

      const isExpired = count === duration;
      if (isExpired) {
        clearInterval(currentFetchInterval);
        this.setState({ currentFetchNode: null });
      }
    }, 1000);
    this.setState({ currentFetchNode: { id: nextNodeId, fetch: fetchNodeInterval } });
  }
}

export const scrollToElement = ({ slug, nodeFetcher, duration, clearNodeFetcher, offsetTop }) => {
  const elementId = slug;
  if (duration && nodeFetcher) return nodeFetcher(elementId, duration, offsetTop);

  const node = document.getElementById(elementId);
  if (!node) return null;
  clearNodeFetcher();
  const nodeTop = node.offsetTop;
  return Scroll.animateScroll.scrollTo(nodeTop + offsetTop);
};

export const hasPrePremiereVideo = item =>
  _.get(item, '0.items.member.0.previewVideo.@populated', false);

export const isInlineComponent = elem => elem && elem.panelType === 'showBranding';
export const isContinueWatchingComponent = elem => elem && elem.panelType === 'continueWatching';
const isVideoComponent = elem => elem && elem.panelType === 'videos';
export const isPromoItem = elem => elem && elem['@type'] === 'Promo';

export const isUngatedVideo = video => {
  const { availabilityTags } = video?.trackingData?.properties || [];
  return (
    availabilityTags &&
    availabilityTags.map(text => text.toLowerCase()).find(text => text === 'free')
  );
};

const isValidPromoItem = elem =>
  elem &&
  elem['@type'] === 'Promo' &&
  (elem.promoType === PROMO_TYPE_NAV_ITEM ||
    elem.promoType === PROMO_TYPE_GALLERY ||
    elem.promoType === 'nftRewards' ||
    elem.promoType === 'nftCountdown') &&
  (elem.url || hasChildren(elem));
export const isCreditsItem = elem => elem && elem['@type'] === PANEL_TYPE_CAST;

export const getCTAUrl = directUrl => {
  if (directUrl && urlIsExternal(directUrl)) return formatDirectUrl(directUrl);
  return directUrl;
};

const isNFT = elem =>
  elem &&
  elem['@type'] === 'Promo' &&
  (elem.promoType === 'nftRewards' || elem.promoType === 'nftCountdown');

export const getAnonymousCTAText = ({ item }) => {
  const {
    seriesDetailAnonymous_promoUngatedCTA,
    seriesDetailAnonymous_promoGatedCTA,
  } = window.Language;
  return isUngatedVideo(item)
    ? seriesDetailAnonymous_promoUngatedCTA
    : seriesDetailAnonymous_promoGatedCTA;
};

export const getAnonymousTuneIn = seriesType => {
  const {
    seriesDetailAnonymous_tuneIn: anonymousTuneInSeries,
    seriesDetailAnonymous_tuneInDocumentary: anonymousTuneInDocumentary,
  } = window.Language;
  if (seriesType === SERIES_TYPE_SPECIAL) {
    return anonymousTuneInDocumentary;
  }
  return anonymousTuneInSeries;
};

export const getTabDataUrl = item => {
  const videos = _.get(item, 'items.member', null);
  if (videos) return null;
  let url = _.get(item, 'episodes.@id', null);
  if (!url) url = _.get(item, 'items.@id', null);
  return url;
};

// @todo: Make it better, instead of checking if the name contains clips & extras
export const isPlaylist = tab => {
  const tabType = _.get(tab, '@type', null);
  const name = _.get(tab, 'name', null);
  return tabType === PANEL_TYPE_CLIPS && name && name.indexOf(LABEL_CLIPS_AND_EXTRAS) === -1;
};

export const isSeasonPanel = panel => _.get(panel, '@type') === PANEL_TYPE_EPISODES;

export const getSeasonCount = ({ tabs }) =>
  tabs ? _.size(_.filter(tabs, t => t['@type'] === 'Season')) : 0;

export const getCreditIdFromUrl = ({ credits, pageId }) => {
  const selectedCredit = _.find(credits, credit => _.get(credit, 'alternateId') === pageId);
  return _.get(selectedCredit, 'id');
};

export const getFeaturedBio = (pageId, bioProfiles) => {
  const pageIdMatch = _.get(bioProfiles, `${pageId}.featured`, null);
  if (pageIdMatch) return pageIdMatch;
  let foundObject = null;
  if (bioProfiles) {
    Object.keys(bioProfiles).forEach(key => {
      const featured = _.get(bioProfiles, `${key}.featured`, null);
      if (pageId === _.get(featured, 'alternateId')) {
        foundObject = featured;
      }
      return null;
    });
  }
  return foundObject;
};

const isAboutSection = section =>
  _.get(section, 'siteScanUrl', '')
    .split('/')
    .pop() === 'about-the-show';

const getSectionOrder = sections => {
  let sectionOrder = [];
  const nftItems = [];
  sections.forEach(section => {
    const type = _.get(section, '@type', '').toLowerCase();
    const promoType = _.get(section, 'promoType', '').toLowerCase();
    const isAbout = isAboutSection(section);
    const isClips = type === 'videolist' || type === 'screenpanel';
    const isHowTo = promoType === 'how-to';
    const isNFTRewards = promoType === 'nftrewards';
    const isNFTCountdown = promoType === 'nftcountdown';
    if (isNFTRewards) nftItems.push({ section, type: 'nftRewards' });
    if (isNFTCountdown) nftItems.push({ section, type: 'nftCountdown' });
    const isUniq = type =>
      type &&
      !_.find(sectionOrder, ({ type: sectionType }) => sectionType === type) &&
      !isHowTo &&
      !isNFTRewards &&
      !isNFTCountdown;
    if (!isClips && isUniq(type)) sectionOrder.push({ section, type });
    if (isClips) sectionOrder.push({ section, type: 'clips' });
    if (isAbout && isUniq('about')) sectionOrder.push({ section, type: 'about' });
  });
  const seasonIndex = sectionOrder.findIndex(elem => elem?.type === 'season');
  const clipsIndex = sectionOrder.findIndex(elem => elem?.type === 'clips');

  if (nftItems.length) nftItems.sort((a, b) => b?.type?.localeCompare(a?.type));

  if (typeof seasonIndex === 'number' && nftItems.length) {
    sectionOrder = [
      ...sectionOrder.slice(seasonIndex, seasonIndex + 1),
      ...nftItems,
      ...sectionOrder.slice(seasonIndex + 1, sectionOrder.length),
    ];
  } else if (
    !typeof seasonIndex === 'number' &&
    typeof clipsIndex === 'number' &&
    nftItems.length
  ) {
    sectionOrder = [
      ...sectionOrder.slice(clipsIndex, clipsIndex + 1),
      ...nftItems,
      ...sectionOrder.slice(clipsIndex + 1, sectionOrder.length),
    ];
  } else if (nftItems.length) {
    sectionOrder = [...nftItems, ...sectionOrder];
  }
  return sectionOrder;
};
const buildArticle = item => {
  const isArticle = isArticleTab(item);
  const articleId = isArticle ? getArticleApiId(item.url) : null;
  return {
    ...item,
    isArticle,
    articleId,
  };
};

const getLinks = sections => {
  const aboutLink = _.filter(sections, section => isAboutSection(section)).map(item =>
    buildArticle(item),
  );
  const links = _.filter(
    sections,
    section => isValidPromoItem(section) && !isAboutSection(section) && !isNFT(section),
  ).map(item => buildArticle(item));
  return aboutLink ? [...links, ...aboutLink] : links;
};

export const getDetailData = ({
  data,
  inlineComponent,
  latestEpisode,
  panels,
  screenTypeId,
  seriesData,
}) => {
  const oldestEpisode = _.get(seriesData, 'oldestEpisode');
  const promos = _.filter(panels, { panelType: PANEL_TYPE_PROMOS });
  const calendar = _.find(_.get(promos, 'items.member'), { promoType: PROMO_TYPE_CALENDAR });
  const latestEpisodeBadges = _.get(latestEpisode, 'contentLabel');
  const isLatestEpisodeLive =
    latestEpisodeBadges && latestEpisodeBadges.find(badge => badge.toLowerCase() === 'live');
  const videoComponents = panels.filter(elem => isVideoComponent(elem));
  const continueWatchingComponents = panels.filter(elem => isContinueWatchingComponent(elem));
  const continueWatchingItems =
    continueWatchingComponents.length > 0
      ? _.get(continueWatchingComponents, '0.items.member')
      : [];
  const continueWatching = continueWatchingItems.slice(0, 1);
  const seo = _.get(data, 'SEO', null);
  const isPersonality = _.get(data, 'categoryType') === 'personality';
  let seriesType;
  switch (screenTypeId) {
    case 'special-detail':
      seriesType = 'special';
      break;
    case 'movie-detail':
      seriesType = 'movie';
      break;
    case 'series-detail':
    default:
      seriesType = 'show';
  }
  let favoritableItems = null;
  if (isPersonality) {
    favoritableItems = _.get(data, 'favoritableItems');
  } else {
    const showBrandingPanel = _.find(panels, isInlineComponent);
    favoritableItems = _.get(showBrandingPanel, 'items.member.0.favoritableItems');
  }
  let tabComponents = panels.filter(
    elem =>
      !isInlineComponent(elem) &&
      !isContinueWatchingComponent(elem) &&
      !isVideoComponent(elem) &&
      !isHiddenForAnonymous(elem),
  );
  if (!isPersonality) {
    tabComponents = _.flatten(tabComponents.map(item => _.get(item, 'items.member')));
  }

  const nonPromoComponents = _.filter(tabComponents, tab => !isPromoItem(tab));
  let promoComponents = _.filter(tabComponents, tab => isValidPromoItem(tab));
  promoComponents = _.sortBy(promoComponents, ['featuredWeight']);
  tabComponents = _.concat(nonPromoComponents, promoComponents);
  tabComponents = _.concat(videoComponents, tabComponents);

  const credits = _.get(inlineComponent, 'items.member.0.credits');

  if (credits && enableBioLanding) {
    credits.id = 'pseudo-tab-credits';
    credits['@type'] = PANEL_TYPE_CAST;
    const castMembersWithImages = _.get(credits, 'member', []).filter(
      item => !!_.get(item, 'images.bioDetail'),
    );
    const castMembers = _.groupBy(castMembersWithImages, 'category');

    credits.member = castMembers;

    if (credits) {
      tabComponents.push(credits);
    }
  }
  const links = getLinks(tabComponents);

  const seriesSortOrder = getSectionOrder(tabComponents);
  return {
    calendar,
    continueWatching,
    continueWatchingItems,
    favoritableItems,
    isLatestEpisodeLive,
    links,
    oldestEpisode,
    promos,
    seo,
    seriesSortOrder,
    seriesType,
    tabComponents,
  };
};

export const getTabsOfMultipleTypes = (tabs = [], types = []) =>
  tabs?.filter(tab => {
    const { '@type': tabType } = tab;
    return _.includes(types, tabType);
  });

export const getSelectedItem = tabs => {
  const path = window.location.pathname;
  const selectedItem = _.find(tabs, tab => {
    const urlMatches = path.includes(tab.url);
    const slugMatches = path.includes(slugify(tab.name));
    return urlMatches || slugMatches;
  });

  return selectedItem || tabs[0];
};
